import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {fetchAndDeserialize, getUrlParameters} from '../../../../../util/api-adapter';

enum VerificationMethodEnum {
    Sms = 'sms',
    Phone = 'phone',
}

const phoneVerificationSchema = r.array(
    r.object({
        detail: r.string(),
        verificationMethod: r.nativeEnum(VerificationMethodEnum),
    })
);

type PhoneVerificationType = r.infer<typeof phoneVerificationSchema>;

function getVerificationNumberUrl(brandId: number, companyId: number | null, chainId?: number) {
    return `/cp/confirmation/yandex/${brandId}/${companyId}/verification_options/${getUrlParameters({
        chain_id: chainId,
    })}`;
}

function fetchVerificationNumber(brandId: number, companyId: number | null, chainId?: number): Promise<unknown> {
    return fetchAndDeserialize(getVerificationNumberUrl(brandId, companyId, chainId), phoneVerificationSchema);
}

export function usePhoneVerification(
    brandId: number,
    companyId: number | null,
    enabled: boolean,
    chainId?: number
): UseQueryResult<PhoneVerificationType> & {smsNumbers: Array<string>; phoneNumbers: Array<string>} {
    const query: UseQueryResult<PhoneVerificationType> = useQuery(
        [getVerificationNumberUrl(brandId, companyId, chainId)],
        () => fetchVerificationNumber(brandId, companyId, chainId),
        {enabled: enabled && companyId !== null}
    );

    const {data} = query;

    const smsNumbers =
        data?.filter((item) => item.verificationMethod === VerificationMethodEnum.Sms).map((item) => item.detail) ?? [];

    const phoneNumbers =
        data?.filter((item) => item.verificationMethod === VerificationMethodEnum.Phone).map((item) => item.detail) ??
        [];

    return {
        smsNumbers,
        phoneNumbers,
        ...query,
    };
}
